import { AlertTriangleIcon, XIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { Button } from '@splitfiesta/ui';

import { usePageLoadComplete } from '@/hooks/usePageLoadComplete';

interface MissingTranslation {
  key: string;
  timestamp: number;
}

// Global store for missing translations
const missingTranslations = new Map<string, MissingTranslation>();
const listeners = new Set<() => void>();
let isCollecting = false;

// Function to start collecting
export function startCollecting() {
  isCollecting = true;
  missingTranslations.clear();
}

// Function to stop collecting
export function stopCollecting() {
  isCollecting = false;
}

// Function to add missing translation
export function reportMissingTranslation(key: string) {
  // Only collect when we're actively collecting
  if (!isCollecting) return;

  if (!missingTranslations.has(key)) {
    missingTranslations.set(key, {
      key,
      timestamp: Date.now(),
    });
    // Defer listener notification to avoid setState during render
    setTimeout(() => {
      listeners.forEach(listener => listener());
    }, 0);
  }
}

export function MissingTranslationsBar() {
  const [translations, setTranslations] = useState<MissingTranslation[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const pageLoadComplete = usePageLoadComplete();
  const location = useLocation();
  const pathname = location.pathname;

  // Reset on pathname change - use separate effect to avoid setting state in response to pathname change
  useEffect(() => {
    // Reset state when pathname changes
    const resetState = () => {
      setShouldShow(false);
      setTranslations([]);
    };

    resetState();
  }, [pathname]);

  useEffect(() => {
    if (!pageLoadComplete) return;

    const updateTranslations = () => {
      setTranslations(Array.from(missingTranslations.values()));
    };

    // Subscribe to updates
    listeners.add(updateTranslations);

    // Start collecting after page is ready
    startCollecting();

    // Force all components to re-render and check translations
    // by dispatching a custom event
    setTimeout(() => {
      window.dispatchEvent(new Event('recheck-translations'));
    }, 100);

    // Wait before showing results
    const showTimer = setTimeout(() => {
      stopCollecting();
      updateTranslations();
      if (missingTranslations.size > 0) {
        setShouldShow(true);
      }
    }, 1000);

    return () => {
      clearTimeout(showTimer);
      listeners.delete(updateTranslations);
      stopCollecting();
    };
  }, [pageLoadComplete, pathname]); // Re-run when these change

  // Only show in development after delay
  if (
    !import.meta.env.DEV ||
    translations.length === 0 ||
    !isVisible ||
    !shouldShow
  ) {
    return null;
  }

  if (isMinimized) {
    return (
      <div className="fixed top-4 right-4 z-50">
        <Button
          variant="danger"
          size="sm"
          onClick={() => setIsMinimized(false)}
          className="p-2 rounded-full shadow-lg animate-pulse relative"
          title={`${translations.length} missing translation${translations.length > 1 ? 's' : ''}`}
        >
          <AlertTriangleIcon className="w-5 h-5" />
          <span className="absolute -top-2 -right-2 bg-red-700 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
            {translations.length}
          </span>
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-500 text-white p-4 shadow-lg">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangleIcon className="w-5 h-5 shrink-0" />
              <h3 className="font-semibold text-lg">
                Missing Translations ({translations.length})
              </h3>
            </div>
            <div className="space-y-1">
              {translations.map(translation => (
                <div
                  key={translation.key}
                  className="font-mono text-sm bg-red-600 bg-opacity-50 px-2 py-1 rounded-sm"
                >
                  {translation.key}
                </div>
              ))}
            </div>
            <p className="text-sm mt-2 opacity-90">
              These translation keys are missing from your locale files.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(true)}
              className="p-1 hover:bg-red-600 rounded-sm text-white"
              title="Minimize"
            >
              <span className="sr-only">Minimize</span>
              <div className="w-4 h-0.5 bg-white"></div>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
              className="p-1 hover:bg-red-600 rounded-sm text-white"
              title="Close"
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
